import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { connect, useDispatch } from 'react-redux';
import { Modal, Dropdown, Tooltip } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Mail, Calendar, Type, ChevronDown, Flag, Hash, DollarSign, LayoutList,
  Star, Phone, AlignLeft, FileText, CheckSquare, Users, Calculator,
  UserRound, MapPin, Clock, Banknote, Percent, Timer, Binary,
  Globe, Palette, Paperclip, Camera, PenLine, User, Link2, CircleDot,
} from 'lucide-react';

import { fieldTypes, COLOUR_PALETTE } from '../../../../js/constants';
import { getRandomColor, getTagColor } from '../../../../js/helper';
import { toggleEditCustomFieldModal } from '../../../../actions/modalAction';
import {
  updateProjectCustomField,
  deleteProjectCustomField,
  getProjectCustomField,
} from '../../../../actions';

const FIELD_ICONS = {
  'status': CircleDot, 'link': Link2, 'date': Calendar, 'text': Type,
  'dropdown': ChevronDown, 'priority': Flag, 'number': Hash, 'cost': DollarSign,
  'multi-select': LayoutList, 'email': Mail, 'rating': Star, 'phone': Phone,
  'long text': AlignLeft, 'rich text': FileText, 'checkbox': CheckSquare,
  'people': Users, 'formula': Calculator, 'full-name': UserRound, 'address': MapPin,
  'datetime': Clock, 'currency': Banknote, 'percentage': Percent, 'duration': Timer,
  'id-reference': Binary, 'country': Globe, 'color': Palette,
  'file-upload': Paperclip, 'camera': Camera, 'signature': PenLine, 'assignee': User,
};

const FIELD_BADGE_STYLES = {
  'status':       { bg: 'rgba(124,58,216,0.1)',  border: 'rgba(124,58,216,0.18)',  color: '#7c3aed' },
  'link':         { bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.18)',  color: '#f97316' },
  'date':         { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)',   color: '#ef4444' },
  'text':         { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#14b8a6' },
  'dropdown':     { bg: 'rgba(236,72,153,0.1)',  border: 'rgba(236,72,153,0.18)',  color: '#ec4899' },
  'priority':     { bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.18)',  color: '#f97316' },
  'number':       { bg: 'rgba(244,63,94,0.1)',   border: 'rgba(244,63,94,0.18)',   color: '#f43f5e' },
  'cost':         { bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.18)',  color: '#10b981' },
  'multi-select': { bg: 'rgba(217,70,239,0.1)',  border: 'rgba(217,70,239,0.18)',  color: '#d946ef' },
  'email':        { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.18)',  color: '#3b82f6' },
  'rating':       { bg: 'rgba(234,179,8,0.1)',   border: 'rgba(234,179,8,0.18)',   color: '#ca8a04' },
  'phone':        { bg: 'rgba(14,165,233,0.1)',  border: 'rgba(14,165,233,0.18)',  color: '#0ea5e9' },
  'long text':    { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.18)',   color: '#16a34a' },
  'rich text':    { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0d9488' },
  'checkbox':     { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.18)',  color: '#2563eb' },
  'people':       { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#0891b2' },
  'formula':      { bg: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.18)',  color: '#4f46e5' },
  'full-name':    { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#06b6d4' },
  'address':      { bg: 'rgba(251,146,60,0.1)',  border: 'rgba(251,146,60,0.18)',  color: '#ea580c' },
  'datetime':     { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#9333ea' },
  'currency':     { bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.18)',  color: '#d97706' },
  'percentage':   { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#7c3aed' },
  'duration':     { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#0891b2' },
  'id-reference': { bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.18)', color: '#475569' },
  'country':      { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0f766e' },
  'color':        { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)',   color: '#dc2626' },
  'file-upload':  { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0d9488' },
  'camera':       { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#9333ea' },
  'signature':    { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#14b8a6' },
  'assignee':     { bg: 'rgba(14,165,233,0.1)',  border: 'rgba(14,165,233,0.18)',  color: '#0ea5e9' },
};
const DEFAULT_BADGE = { bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.18)', color: '#4f46e5' };

const OPTION_SWATCHES = COLOUR_PALETTE;

// ─── Drag-and-drop option row ─────────────────────────────────────────────────
const ColorPickerPortal = ({ anchorRef, color, onSelect, onClose }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left - 4 });
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e) => {
      if (!anchorRef.current?.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return createPortal(
    <div
      className="p-2 rounded-xl shadow-2xl"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, background: 'white', border: '1px solid rgba(99,102,241,0.15)', width: 152 }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="grid grid-cols-5 gap-1.5">
        {OPTION_SWATCHES.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => { onSelect(c); onClose(); }}
            className="w-5 h-5 rounded-full transition-transform hover:scale-125"
            style={{ background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: 2 }}
          />
        ))}
      </div>
    </div>,
    document.body,
  );
};

const OptionRow = React.memo(({ option, index, draggingId, onLabelChange, onColorChange, onDelete }) => {
  const color = getTagColor(option.metadata?.color) || option.metadata?.color || '#ccc';
  const draggableId = String(option?.id ?? option?._id ?? option?.key ?? `${option?.label}-${index}`);
  const [local, setLocal] = useState(option.label || '');
  const [showPicker, setShowPicker] = useState(false);
  const dotRef = useRef(null);

  useEffect(() => { setLocal(option.label || ''); }, [option.label]);

  const commit = useCallback(() => {
    const v = local.trim();
    if (!v) onDelete(index);
    else onLabelChange(index, v);
  }, [local, index, onLabelChange, onDelete]);

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="draggable-customfield-row flex items-center gap-2.5 py-2 pr-3 group"
          style={{
            ...provided.draggableProps.style,
            borderBottom: snapshot.isDragging ? 'none' : '1px solid rgba(99,102,241,0.06)',
            paddingLeft: 8,
            background: snapshot.isDragging ? 'rgba(99,102,241,0.09)' : '',
            borderRadius: snapshot.isDragging ? 8 : 0,
            boxShadow: snapshot.isDragging ? '0 4px 16px rgba(99,102,241,0.18)' : 'none',
            border: snapshot.isDragging ? '1px solid rgba(99,102,241,0.3)' : undefined,
          }}
        >
          <div
            className={`customfield-options-row-drag-indicator flex-shrink-0${snapshot.isDragging ? ' isDragging' : draggingId && draggingId !== draggableId ? '' : ' hover-drag-icon'}`}
            {...provided.dragHandleProps}
          >
            <DragIndicatorIcon style={{ fontSize: 15, color: 'rgba(99,102,241,0.3)' }} />
          </div>
          <button
            ref={dotRef}
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => setShowPicker(p => !p)}
            className="w-3.5 h-3.5 rounded-full flex-shrink-0 hover:scale-125 transition-transform"
            style={{ background: color, boxShadow: showPicker ? `0 0 0 3px ${color}44` : undefined }}
            title="Change color"
          />
          {showPicker && (
            <ColorPickerPortal
              anchorRef={dotRef}
              color={color}
              onSelect={(c) => onColorChange(index, c)}
              onClose={() => setShowPicker(false)}
            />
          )}
          <input
            className="flex-1 text-[13px] bg-transparent py-0.5 px-1 outline-none border-b border-transparent transition-colors"
            style={{ color: 'rgba(15,15,25,0.75)' }}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.4)'}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
          />
          <button
            type="button"
            onClick={() => onDelete(index)}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-[11.5px] font-semibold px-2 py-0.5 rounded-md"
            style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.15)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.14)'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.08)'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.15)'; }}
          >
            Delete
          </button>
        </div>
      )}
    </Draggable>
  );
});

// ─── Reusable options editor ───────────────────────────────────────────────────
const OptionsEditor = ({ options, setOptions, intl }) => {
  const [input, setInput] = useState('');
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => {
    const needs = options.some((o) => !o.metadata?.color);
    if (needs) {
      setOptions((prev) =>
        prev.map((o, i) => o.metadata?.color ? o : { ...o, metadata: { ...o.metadata, color: getRandomColor(i) } }),
      );
    }
  }, [options.length]);

  const add = () => {
    if (!input.trim()) return;
    setOptions((prev) => [
      ...prev,
      { key: Math.random(), id: `opt-${Date.now()}`, label: input.trim(), metadata: { color: getRandomColor(prev.length) } },
    ]);
    setInput('');
  };

  return (
    <div>
      <div className="rounded-xl overflow-hidden mb-2.5" style={{ border: '1px solid rgba(99,102,241,0.14)' }}>
        <DragDropContext
          onDragStart={(s) => setDraggingId(s.draggableId)}
          onDragEnd={(r) => {
            setDraggingId(null);
            if (!r.destination || r.destination.index === r.source.index) return;
            setOptions((prev) => {
              const arr = [...prev];
              const [moved] = arr.splice(r.source.index, 1);
              arr.splice(r.destination.index, 0, moved);
              return arr;
            });
          }}
        >
          <div className="max-h-44 overflow-y-auto">
            <Droppable droppableId="edit-modal-opts">
              {(p) => (
                <div ref={p.innerRef} {...p.droppableProps}>
                  {options.map((opt, i) => (
                    <OptionRow
                      key={opt?.id ?? opt?._id ?? opt?.key ?? `${opt?.label}-${i}`}
                      option={opt}
                      index={i}
                      draggingId={draggingId}
                      onLabelChange={(idx, val) => setOptions((prev) => { const n = [...prev]; n[idx] = { ...n[idx], label: val }; return n; })}
                      onColorChange={(idx, c) => setOptions((prev) => { const n = [...prev]; n[idx] = { ...n[idx], metadata: { ...n[idx].metadata, color: c } }; return n; })}
                      onDelete={(idx) => setOptions((prev) => { const n = [...prev]; n.splice(idx, 1); return n; })}
                    />
                  ))}
                  {p.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={`${intl.formatMessage({ id: 'INPUT_OPTION', defaultMessage: 'Add option' })} ${options.length + 1}`}
          className="flex-1 h-9 px-3 text-[13px] rounded-xl bg-white outline-none transition-all"
          style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.75)' }}
          onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
          onBlur={e => { e.target.style.borderColor = 'rgba(99,102,241,0.2)'; add(); }}
        />
        <button
          type="button"
          onClick={add}
          className="h-9 w-9 flex items-center justify-center text-white text-lg font-medium rounded-xl transition-all flex-shrink-0"
          style={{ background: '#4f46e5', boxShadow: '0 2px 6px rgba(79,70,229,0.3)' }}
          onMouseEnter={e => e.currentTarget.style.background = '#4338ca'}
          onMouseLeave={e => e.currentTarget.style.background = '#4f46e5'}
        >
          +
        </button>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const EditCustomFieldModal = ({ customField, isModalOpen, workspace, project }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [originalType, setOriginalType] = useState('text');
  const [typeAcknowledged, setTypeAcknowledged] = useState(false);
  const [options, setOptions] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const ft = fieldTypes.find((f) => f.key === fieldType);
  const TriggerIcon = ft ? (FIELD_ICONS[ft.key] || Hash) : null;
  const headerStyle = FIELD_BADGE_STYLES[fieldType] || DEFAULT_BADGE;
  const HeaderIcon = FIELD_ICONS[fieldType] || Hash;

  const requiresOpts = ['dropdown', 'status', 'multi-select'].includes(fieldType);
  const typeChanged = fieldType !== originalType;
  const isDisabled = !fieldName.trim() || (requiresOpts && options.length === 0) || (typeChanged && !typeAcknowledged);

  const projectId = project?._id || customField?.project?._id || customField?.project;

  useEffect(() => {
    if (!customField || !isModalOpen) return;
    setFieldName(customField.name || '');
    setFieldType(customField.type || 'text');
    setOriginalType(customField.type || 'text');
    setTypeAcknowledged(false);
    setOptions(customField.possibleValues || []);
    setDeleteConfirm(false);
  }, [customField, isModalOpen]);

  const closeModal = () => dispatch(toggleEditCustomFieldModal(false));

  const handleSave = () => {
    if (isDisabled) return;
    const cleanOpts = options.map(({ key: _k, ...rest }) => rest);
    dispatch(
      updateProjectCustomField(
        customField?._id,
        { name: fieldName.trim(), possibleValues: cleanOpts, projectId, type: fieldType },
        () => dispatch(getProjectCustomField(projectId)),
      ),
    );
    closeModal();
  };

  const handleDeleteConfirm = () => {
    dispatch(
      deleteProjectCustomField(
        customField?._id,
        projectId,
        () => dispatch(getProjectCustomField(projectId)),
        intl,
      ),
    );
    closeModal();
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={closeModal}
      width={460}
      centered
      footer={null}
      styles={{
        body: { padding: 0 },
        header: { padding: '20px 24px 16px', borderBottom: '1px solid rgba(99,102,241,0.1)', marginBottom: 0 },
        content: { borderRadius: 16, overflow: 'hidden', padding: 0 },
      }}
      title={
        <span className="text-[15px] font-semibold" style={{ color: 'rgba(15,15,25,0.85)' }}>
          <FormattedMessage id="EDIT_CUSTOM_FIELD" defaultMessage="Edit Custom Field" />
        </span>
      }
    >
      <div className="flex flex-col" style={{ maxHeight: 580 }}>
        {/* Field identity header */}
        <div className="flex items-center gap-3 px-6 py-3.5" style={{ background: 'rgba(99,102,241,0.04)', borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: headerStyle.bg, border: `1px solid ${headerStyle.border}` }}>
            <HeaderIcon size={17} color={headerStyle.color} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold truncate" style={{ color: 'rgba(15,15,25,0.85)' }}>{customField?.name}</div>
            <div className="text-[11.5px] capitalize mt-0.5" style={{ color: 'rgba(15,15,25,0.4)' }}>{fieldType} Field</div>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-white">
          {/* Field name */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(15,15,25,0.35)' }}>
              <FormattedMessage id="LABEL" defaultMessage="Label" />
            </label>
            <input
              type="text"
              autoFocus
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !isDisabled) handleSave(); }}
              className="w-full h-10 px-3 text-[13px] rounded-xl bg-white outline-none transition-all"
              style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.82)', boxShadow: '0 1px 3px rgba(99,102,241,0.06)' }}
              onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
              onBlur={e => e.target.style.borderColor = 'rgba(99,102,241,0.2)'}
            />
          </div>

          {/* Field type */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(15,15,25,0.35)' }}>
              <FormattedMessage id="FIELD_TYPE" defaultMessage="Field Type" />
            </label>
            <Dropdown
              trigger={['click']}
              dropdownRender={(menu) => (
                <div style={{ borderRadius: 12, border: '1px solid rgba(99,102,241,0.14)', boxShadow: '0 8px 24px rgba(99,102,241,0.10)', overflow: 'hidden', maxHeight: 240, overflowY: 'auto', background: 'white' }}>
                  {menu}
                </div>
              )}
              menu={{
                style: { boxShadow: 'none', border: 'none', borderRadius: 0 },
                items: fieldTypes.map((item) => {
                  const DIcon = FIELD_ICONS[item.key] || Hash;
                  return {
                    key: item.key,
                    label: (
                      <span className="flex items-center gap-2 text-[13px]" style={{ color: 'rgba(15,15,25,0.75)' }}>
                        <DIcon size={14} strokeWidth={1.5} color="rgba(15,15,25,0.45)" style={{ flexShrink: 0 }} />
                        {intl.formatMessage({ id: `${item.label}`.replaceAll(' ', '_').toUpperCase(), defaultMessage: item.label })}
                      </span>
                    ),
                    onClick: () => {
                      const next = fieldTypes.find((f) => f.key === item.key);
                      setFieldType(item.key);
                      setTypeAcknowledged(false);
                      if (next?.options && options.length === 0) setOptions(next.options);
                    },
                  };
                }),
              }}
              placement="bottom"
            >
              <button
                type="button"
                className="w-full h-10 flex items-center justify-between px-3 text-[13px] rounded-xl bg-white outline-none transition-all"
                style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.75)', boxShadow: '0 1px 3px rgba(99,102,241,0.06)' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.45)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'}
              >
                <span className="flex items-center gap-2">
                  {TriggerIcon && <TriggerIcon size={15} strokeWidth={1.5} color="rgba(15,15,25,0.5)" />}
                  <FormattedMessage id={`${fieldType}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={fieldType} />
                </span>
                <ChevronDown size={12} color="rgba(15,15,25,0.4)" />
              </button>
            </Dropdown>

            {/* Type change warning */}
            {typeChanged && (
              <div className="mt-3 rounded-xl p-3 space-y-2.5" style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <div className="flex items-start gap-2">
                  <svg className="flex-shrink-0 mt-0.5" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(185,28,28,0.9)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <div className="text-xs leading-relaxed" style={{ color: 'rgba(185,28,28,0.9)' }}>
                    <strong>Changing the field type will erase all existing data</strong> stored in this field across every task. This cannot be undone.
                  </div>
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={typeAcknowledged}
                    onChange={(e) => setTypeAcknowledged(e.target.checked)}
                    className="w-3.5 h-3.5 rounded accent-red-600"
                  />
                  <span className="text-xs font-medium" style={{ color: 'rgba(185,28,28,0.85)' }}>
                    I understand — existing data will be permanently lost
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Options editor */}
          {requiresOpts && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(15,15,25,0.35)' }}>
                <FormattedMessage id="OPTIONS" defaultMessage="Options" />
                {options.length > 0 && (
                  <span className="font-normal normal-case tracking-normal ml-1" style={{ color: 'rgba(99,102,241,0.5)' }}>({options.length})</span>
                )}
              </label>
              <OptionsEditor options={options} setOptions={setOptions} intl={intl} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 bg-white" style={{ borderTop: '1px solid rgba(99,102,241,0.08)' }}>
          {deleteConfirm ? (
            <div className="space-y-3">
              <p className="text-[13px] font-medium text-center" style={{ color: 'rgba(15,15,25,0.7)' }}>
                Delete <strong>{customField?.name}</strong>? This cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-xl text-[13px] font-semibold transition-colors"
                  style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.6)', background: 'white' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 rounded-xl text-[13px] font-semibold text-white transition-colors"
                  style={{ background: '#dc2626' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'}
                  onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(true)}
                className="px-4 py-2 text-[13px] font-semibold rounded-xl transition-colors"
                style={{ color: '#dc2626', border: '1px solid rgba(220,38,38,0.2)', background: 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <FormattedMessage id="DELETE_FIELD" defaultMessage="Delete Field" />
              </button>
              <button
                type="button"
                disabled={isDisabled}
                onClick={handleSave}
                className="flex-1 py-2 rounded-xl text-[13px] font-semibold transition-all"
                style={isDisabled
                  ? { background: 'rgba(99,102,241,0.1)', color: 'rgba(99,102,241,0.35)', cursor: 'not-allowed' }
                  : { background: '#4f46e5', color: 'white', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }
                }
                onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.background = '#4338ca'; }}
                onMouseLeave={e => { if (!isDisabled) e.currentTarget.style.background = '#4f46e5'; }}
              >
                <FormattedMessage id="SAVE_CHANGES" defaultMessage="Save Changes" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  workspace: state.workspaceReducer.currentWorkspace,
});

export default connect(mapStateToProps)(EditCustomFieldModal);
